import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import RequireAuth from "components/RequireAuth";
import OnboardingGate from "components/ui/OnboardingGate";
import NotFound from "pages/NotFound";
import CustomerAnalytics from './pages/customer-analytics';
import SalesPerformance from './pages/sales-performance';
import ExecutiveOverview from './pages/executive-overview';
import Leads from './pages/leads';
import Contacts from './pages/contacts';
import CustomerDetail from './pages/customer-detail';
import Inbox from './pages/inbox';
import Today from './pages/today';
import Approvals from './pages/approvals';
import Tasks from './pages/tasks';
import SalesPipeline from './pages/sales-pipeline';
import Opportunities from './pages/opportunities';
import Onboarding from './pages/onboarding';
import PilotOverview from './pages/pilot';
import Team from './pages/team';
import AcceptInvite from './pages/accept-invite';
import Activity from './pages/activity';
import Settings from './pages/settings';
import Operations from './pages/operations';
import Integrations from './pages/integrations';
import Invoices from './pages/invoices';
import Login from './pages/login';
import Signup from './pages/signup';

const guard = (element) => <RequireAuth>{element}</RequireAuth>;
// Sprint 9 (Part 89 §89.5): dashboard routes also require onboarding setup
// (workspace → WhatsApp → data). The first follow-up nudge does not gate
// the dashboard (Sprint 15 QA).
const guarded = (element) => (
  <RequireAuth>
    <OnboardingGate>{element}</OnboardingGate>
  </RequireAuth>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        <Route path="/" element={<Navigate to="/today" replace />} />
        <Route path="/onboarding" element={guard(<Onboarding />)} />
        <Route path="/today" element={guarded(<Today />)} />
        <Route path="/executive-overview" element={guarded(<ExecutiveOverview />)} />
        <Route path="/customer-analytics" element={guarded(<CustomerAnalytics />)} />
        <Route path="/sales-performance" element={guarded(<SalesPerformance />)} />
        <Route path="/leads" element={guarded(<Leads />)} />
        <Route path="/contacts" element={guarded(<Contacts />)} />
        <Route path="/contacts/:id" element={guarded(<CustomerDetail />)} />
        <Route path="/invoices" element={guarded(<Invoices />)} />
        <Route path="/inbox" element={guarded(<Inbox />)} />
        <Route path="/tasks" element={guarded(<Tasks />)} />
        <Route path="/approvals" element={guarded(<Approvals />)} />
        <Route path="/sales-pipeline" element={guarded(<SalesPipeline />)} />
        <Route path="/opportunities" element={guarded(<Opportunities />)} />
        {/* Sprint 10 (Part 90 §90.2): OWNER-only pilot ops view */}
        <Route path="/pilot" element={guarded(<PilotOverview />)} />
        {/* Sprint 11 (Part 91 §91.1): team & invites; role-gated inside the page */}
        <Route path="/team" element={guarded(<Team />)} />
        {/* Sprint 12 (Part 92 §92.1): org-wide activity trail, any role */}
        <Route path="/activity" element={guarded(<Activity />)} />
        {/* Sprint 13 (Part 93 §93.1): workspace settings (Owner edits, Manager/Rep view) */}
        <Route path="/settings" element={guarded(<Settings />)} />
        {/* Sprint 14 (Part 94 §94.2): operations SLA report */}
        <Route path="/operations" element={guarded(<Operations />)} />
        {/* Sprint 15 QA: lead-source integrations center */}
        <Route path="/integrations" element={guarded(<Integrations />)} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        {/* Sprint 11 (Part 91 §91.1): public invite acceptance */}
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
